import React, { ChangeEvent, FormEvent, useContext, useEffect, useState } from 'react'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import {
  doReportsSearch,
  getInvalidReportSearchQueryMessage,
  getVerdictLabel,
  uniqueVerdicts,
} from 'app/modules/reports/helpers'
import { applicationConfiguration, MainNotificationContext } from 'app/modules/shared'
import { SearchParams, SignalVerdict } from 'app/modules/reports/types'
import { Form } from 'react-bootstrap'
import Button from 'app/modules/shared/components/button'
import { useNavigate, useLocation } from 'react-router'
import TextInput from 'app/components/text-input'
import Checkbox from 'app/components/checkbox'
import DatePicker from 'app/components/datepicker/datepicker'
import { PickerDate, Range } from 'app/components/datepicker/types'
import { MultiInput } from './multi-input'
import RangeInput from '../../components/range-input'
import FilterSummary from './filter-summary'
import { searchService } from '../../services/search.service'
import DropdownSelect from 'app/components/dropdown'
import YaraSearch from './full-page-search-forms/yara-rules-search'
import ThreatIndicatorsSearch from './full-page-search-forms/threat-indicators-search'
import MitreSearch from './full-page-search-forms/mitre-search'
import { LabelWithIcon } from '../../components'
import { logger } from 'app/components'

const ThreatHunting = () => {
  const { _t } = useTypedTranslation()
  const navigate = useNavigate()
  const config = useContext(applicationConfiguration)
  const mainNotification = useContext(MainNotificationContext)
  const isSearchBlocked = config.maintenance.blockSearch

  const [filters, setFilters] = useState<SearchParams>({
    derived_files: true,
    unique_files: false,
    method: 'and',
  })

  const [fileTypeDropdownKey, setFileTypeDropdownKey] = useState(0)
  const [rangeInputKey, setRangeInputKey] = useState(0)
  const [datePickerKey, setDatePickerKey] = useState(0)
  const [selectedDateRange, setSelectedDateRange] = useState<Range<PickerDate>>([null, null])
  const [threatAttributionOptions, setThreatAttributionOptions] = useState<{ label: string; value: string }[]>([])

  useEffect(() => {
    const fetchThreatNames = async () => {
      try {
        const { tags } = await searchService.getThreatAttributionOptions()
        setThreatAttributionOptions(tags.map((tag) => ({ label: tag, value: tag })))
      } catch (e) {
        logger.error(e)
      }
    }

    fetchThreatNames()
  }, [])

  const onClear = () => {
    setFilters({
      query: '',
      filetype: '',
      media_type: '',
      verdict: '',
      filedesc: '',
      tags: '',
      threat_attribution: '',
      yara_rule: '',
      date_from: '',
      date_to: '',
      rate_from: '',
      rate_to: '',
      host: '',
      domain: '',
      imphash: '',
      ssdeep: '',
      authentihash: '',
      fuzzyfsiohash: '',
      source_sha256: '',
      source_md5: '',
      ip: '',
      url: '',
      uuid: '',
      registry_path: '',
      revision_save_id: '',
      sha1: '',
      sha256: '',
      sha512: '',
      md5: '',
      unique_files: false,
      method: 'and',
      signal_groups: '',
      retry_count: '',
      mitre_techniques: '',
      derived_files: true,
    })
    setSelectedDateRange([null, null])
    setDatePickerKey((prev) => prev + 1)
  }

  const sourceTypes = ['url', 'url-to-file', 'file']
  const generalTypes = [
    { value: 'ms-office', name: 'MS Office files', type: 'General type' },
    { value: 'exe', name: 'Executable files', type: 'General type' },
    { value: 'script', name: 'Script files', type: 'General type' },
    { value: 'mail', name: 'E-Mail files', type: 'General type' },
  ]
  const specificTypes = config.supportedTypes
    .filter((ext) => !generalTypes.some((type) => type.value === ext))
    .map((type) => ({ value: type, name: type, type: 'Specific type' }))
  const types = { 'General type': generalTypes, 'Specific type': specificTypes }

  const handleVerdictFilterChange = (verdict: SignalVerdict) => {
    setFilters({ ...filters, verdict: getUpdatedVerdictList(filters.verdict, verdict) })
  }

  const handleDateFilterChange = (dateRange: Range<PickerDate>) => {
    const dateTo = formatSearchDate(dateRange[1])

    setFilters({
      ...filters,
      date_from: formatSearchDate(dateRange[0]),
      date_to: dateTo != '' ? dateTo : new Date().toISOString().split('T')[0],
    })
  }

  const handleRateFilterChange = (target: 'from' | 'to', value: number | string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [`rate_${target}`]: value.toString(),
    }))
  }

  const handleSourceTypeFilterChange = (sourceType: string) => {
    setFilters((prev) => ({
      ...prev,
      source_type: prev.source_type === sourceType ? '' : sourceType,
    }))
  }

  const handleFullPageFilterChange = (key: string, value: string[]) =>
    setFilters({ ...filters, [key]: value.join(',') })

  const handleFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { id: key, value } = event.currentTarget as HTMLInputElement

    setFilters({
      ...filters,
      [key]: value,
    })
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    const validationError = getInvalidReportSearchQueryMessage(filters.query)

    if (validationError) {
      mainNotification.setMessageData(validationError, 'danger')
      return
    }

    doReportsSearch(filters, navigate)
  }

  const handleRemoveFilter = (filterName: string, filterValue: string | null = null) => {
    // filterValue only passed for lists

    if (filterName === 'filetype') {
      // Key change causing the dropdown to rerender -> makes the dropdown empty
      setFileTypeDropdownKey((prev) => prev + 1)
    }

    if (filterName === 'date_from') {
      setDatePickerKey((prev) => prev + 1)
    }

    if (filterValue) {
      const filterKey = filterName as keyof SearchParams

      return setFilters((prevFilters) => ({
        ...prevFilters,
        [filterKey]: (prevFilters[filterKey] as string)
          .split(',')
          .filter((value) => value !== filterValue)
          .join(','),
      }))
    }

    if (filterName.startsWith('date') || filterName.startsWith('rate')) {
      const filterNamePart = filterName.split('_')[0]

      return setFilters((prevFilters) => ({
        ...prevFilters,
        [`${filterNamePart}_from`]: '',
        [`${filterNamePart}_to`]: '',
      }))
    }

    setFilters((prevFilters) => ({ ...prevFilters, [filterName]: '' }))
  }

  const defaultInputField = (id: string, labelKey: string) => (
    <div className="search-input-block">
      <p className="text-note text-m m-0 pb-1">{_t(labelKey)}</p>
      <TextInput
        id={id}
        value={filters[id as keyof typeof filters] as string | number | JSX.Element | undefined}
        onChange={handleFilterChange}
      />
    </div>
  )

  // Load filter params when navigating back from search-result
  const location = useLocation()

  useEffect(() => {
    // Reset on refresh
    navigate(location.pathname, {})

    const state = location.state as { filters?: SearchParams } | null

    if (state?.filters) {
      if (state.filters.date_from) {
        const date_from: Date = new Date(state.filters.date_from)
        const date_to: Date | null = state.filters.date_to ? new Date(state.filters.date_to) : null
        setSelectedDateRange([date_from, date_to])
      }

      setFilters(state.filters)
      setFileTypeDropdownKey((prev) => prev + 1)
      setRangeInputKey((prev) => prev + 1)
    }
  }, [location.state])

  return (
    <Form onSubmit={handleSubmit}>
      <div className="container">
        <div className="d-flex-ds3 flex-column">
          <h3 className="m-0">{_t('base-filters')}</h3>
          <div className="d-block">
            <p className="text-note text-m m-0 pb-1">{_t('search')}</p>
            <TextInput
              id="query"
              className="align-self-center"
              placeholder={_t('search-bar-placeholder')}
              value={filters.query}
              onChange={handleFilterChange}
              prefix={<i className="ds3-icon ds3-search" />}
              // postfix={<i className="ds3 ds3-help" />} // Disabled helper until having helper text
              clearable
              size="big"
            />
          </div>
          <div className="search-inputs-container">
            <div className="search-inputs-verdict">
              <p className="text-note text-m m-0 pb-1">{_t('verdict')}</p>
              <div className="verdict-checkbox-container">
                {Object.values(uniqueVerdicts())
                  .reverse()
                  .map((verdict) => (
                    <Checkbox
                      key={verdict}
                      onChange={() => handleVerdictFilterChange(verdict)}
                      checked={filters.verdict?.split(',').includes(verdict.toLowerCase())}
                      id={`verdict-${verdict}`}
                      label={getVerdictLabel(verdict, config.useHumanFriendlyVerdicts)}
                      variant={verdict}
                    />
                  ))}
              </div>
            </div>
            <div className="search-inputs-date">
              <p className="text-note text-m m-0 pb-1">{_t('date')}</p>
              <DatePicker
                key={datePickerKey}
                defaultValue={selectedDateRange}
                handleDateChange={handleDateFilterChange}
              />
            </div>
          </div>
          <div className="search-inputs-container">
            <div className="search-input-block">
              <p className="text-note text-m m-0 pb-1">{_t('tags')}</p>
              <MultiInput
                inputClass="m-0 p-0"
                value={filters.tag}
                onChange={(value) => setFilters({ ...filters, tag: value })}
                addPrefix={_t('add-tag')}
              />
            </div>
            <div className="search-input-block">
              <p className="text-note text-m m-0 pb-1">{_t('threat-attribution')}</p>
              <MultiInput
                inputClass="m-0 p-0"
                value={filters.threat_attribution}
                options={threatAttributionOptions}
                showList
                onChange={(value) => setFilters({ ...filters, threat_attribution: value })}
                addPrefix={_t('add-threat-attribution')}
                placeholder="select-threat-name"
                noOptionsMessage="no-threat-attribution"
              />
            </div>
            <div className="search-input-block">
              <p className="text-note text-m m-0 pb-1">{_t('filetype')}</p>
              <DropdownSelect
                key={fileTypeDropdownKey}
                id="file-type"
                searchable
                value={filters.filetype}
                groupedItems={types}
                onClick={(value) => setFilters({ ...filters, filetype: value })}
              />
            </div>
            <div className="search-input-block">
              <p className="text-note text-m m-0 pb-1">{_t('source-type')}</p>
              <div className="source-type-input">
                {sourceTypes.map((sourceType) => (
                  <Button
                    id={`threat-hunting-source-type-${sourceType}`}
                    key={sourceType}
                    className={`source-type-button ${filters.source_type === sourceType ? 'source-type-selected' : ''}`}
                    variant="secondary"
                    onClick={() => handleSourceTypeFilterChange(sourceType)}
                  >
                    {_t(sourceType)}
                  </Button>
                ))}
              </div>
            </div>
            <div key={rangeInputKey} className="search-input-block">
              <p className="text-note text-m m-0 pb-1">{_t('detection-rate')}</p>
              <RangeInput
                from={Number(filters.rate_from)}
                to={Number(filters.rate_to)}
                controlRange={handleRateFilterChange}
                enableSwitch
                isEnabled={!!filters.rate_from}
              />
            </div>
          </div>
        </div>
        <div className="d-flex-ds3 flex-column ioc-search-group">
          <h3 className="m-0">{_t('ioc')}</h3>
          <div className="search-inputs-container">
            <div className="search-input-block">
              <YaraSearch
                onApply={(rules: string[]) => handleFullPageFilterChange('yara_rule', rules)}
                searchRules={filters.yara_rule ? filters.yara_rule.split(',') : []}
                filterId="yara_rule"
                value={filters.yara_rule}
              />
            </div>
            <div className="search-input-block">
              <ThreatIndicatorsSearch
                onApply={(indicators: string[]) => handleFullPageFilterChange('signal_groups', indicators)}
                searchIndicators={filters.signal_groups ? filters.signal_groups.split(',') : []}
                filterId="signal_groups"
                value={filters.signal_groups}
              />
            </div>
            <div className="search-input-block">
              <MitreSearch
                onApply={(techniques: string[]) => handleFullPageFilterChange('mitre_techniques', techniques)}
                searchTechniques={filters.mitre_techniques ? filters.mitre_techniques.split(',') : []}
                filterId="mitre_techniques"
                value={filters.mitre_techniques}
              />
            </div>
          </div>
          <div className="search-inputs-container">
            {defaultInputField('uuid', 'UUID')}
            {defaultInputField('registry_path', 'registry-path')}
            {defaultInputField('revision_save_id', 'revision-save-id')}
          </div>
        </div>
        <div className="d-flex-ds3 flex-column ioc-search-group">
          <h3 className="m-0">{_t('hashes')}</h3>
          <div className="search-inputs-container">
            {defaultInputField('media_type', 'media-type')}
            {defaultInputField('imphash', 'imphash')}
            {defaultInputField('ssdeep', 'ssdeep')}
          </div>
          <div className="search-inputs-container">
            {defaultInputField('authentihash', 'authentihash')}
            {defaultInputField('fuzzyfsiohash', 'fsiofuzzyhash')}
            <div className="search-input-block">{/*Placeholder to fill up space*/}</div>
          </div>
        </div>

        <div className="d-flex-ds3 flex-column mt-10">
          <h3 className="m-0">{_t('advanced')}</h3>

          <div className="d-flex flex-wrap gap-4">
            <div className="search-input-block" style={{ flex: '0 0 250px' }}>
              <p className="text-note text-m mb-3 d-flex justify-content-start gap-2">
                {_t('derived-files')}
                <LabelWithIcon
                  note="search-derived-files-note"
                  noteId="derived-files"
                  trigger={['hover', 'focus']}
                  variant="light"
                />
              </p>
              <Checkbox
                onChange={(checked) => setFilters({ ...filters, derived_files: checked })}
                checked={
                  typeof filters.derived_files === 'boolean' ? filters.derived_files : filters.derived_files === 'true'
                }
                id="derived-files"
                label={_t('search-in-derived-files')}
              />
            </div>
            <div className="search-input-block" style={{ flex: '0 0 250px' }}>
              <p className="text-note text-m mb-3 d-flex justify-content-start gap-2">
                {_t('unique-files')}
                <LabelWithIcon
                  note="search-unique-files-note"
                  noteId="unique-files"
                  trigger={['hover', 'focus']}
                  variant="light"
                />
              </p>
              <Checkbox
                onChange={(checked) => setFilters({ ...filters, unique_files: checked })}
                checked={
                  typeof filters.unique_files === 'boolean' ? filters.unique_files : filters.unique_files === 'true'
                }
                id="unique-files"
                label={_t('show-only-unique-files')}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="threat-hunting-footer">
        <div className="threat-hunting-filters">
          <FilterSummary filters={filters} allowShort onRemoveFilter={handleRemoveFilter} />
        </div>
        <div className="threat-hunting-button-group">
          <Button id="threat-hunting-clear" className="fixed" variant="tertiary" onClick={onClear}>
            {_t('clear')}
          </Button>
          <Button
            id="threat-hunting-show-reports"
            type="submit"
            className="fixed"
            variant="primary"
            disabled={!checkFormHasValues(filters) || isSearchBlocked}
          >
            {_t('show-reports')}
          </Button>
        </div>
      </div>
    </Form>
  )
}

export default ThreatHunting

/**
 *
 * @param verdictList     Comma separated of selected verdicts
 * @param actualVerdict   _verdict_ - Add/remove _verdict_ to selected verdicts
 * @returns               Updated list of selected verdicts
 */
export const getUpdatedVerdictList = (verdictList: string | undefined, actualVerdict: string): string => {
  const verdictArray = verdictList ? verdictList.split(',') : []
  const index = verdictArray.indexOf(actualVerdict)

  if (index !== -1) {
    return verdictArray.filter((verdict) => verdict !== actualVerdict).join(',')
  }

  if (typeof actualVerdict !== 'undefined') {
    return [...verdictArray, actualVerdict].join(',')
  }

  return verdictArray.join(',')
}

/**
 * Check if any of the filters has value
 */
const checkFormHasValues = (filters: { [key: string]: any }) => {
  let hasValues = false

  Object.keys(filters).forEach((key) => {
    const checkField = ['unique_files', 'method', 'derived_files'].indexOf(key) === -1
    if (checkField && filters[key as keyof typeof filters] !== '') {
      hasValues = true
    }
  })

  return hasValues
}

/**
 * Formats the date to match backend expectations
 */
export const formatSearchDate = (date: PickerDate) => {
  return !date
    ? ''
    : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/**
 * Convert date in format YYYY-MM-DD to Date object
 */
export function searchDateToObj(dateTo: string | undefined): Date | null {
  if (!dateTo) {
    return null
  }

  const parts = dateTo.split('-')

  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
}
